import { CellContext, Row } from "@tanstack/react-table";
import { formatBlockTimeDisplay } from "../../../../../utils/flights/flight-table/FlightTableHelper";
import FlightNumberCellLink from "../FlightNumberCellLink";

/**
 * Enhances the configuration of flight table columns by applying custom logic
 * based on the column ID. Specific transformations are applied to certain
 * column IDs such as "common.block time", "arrival.flight number",
 * "departure.flight number", and "common.flight number".
 */
export const enhanceFlightColumnConfig = (
  columns: any[],
  airport: string,
  currentAirportTime: Date
) => {
  return columns.map((column) => {
    switch (column.id) {
      case "common.block time":
        return updateBlockTimeColumnConfig(column, currentAirportTime);

      case "arrival.flight number":
        return updateFlightNumberColumnConfig(column, airport, "arrival");

      case "departure.flight number":
        return updateFlightNumberColumnConfig(column, airport, "departure");

      case "common.flight number":
        return updateFlightNumberColumnConfig(column, airport);

      default:
        return column;
    }
  });
};

/**
 * Updates the configuration of a column to include custom logic for displaying and filtering
 * block time values. This function modifies the `cell` and `filterFn` properties of the column
 * configuration to handle the formatting and filtering of block time data.
 */
export const updateBlockTimeColumnConfig = (
  column: any,
  currentAirportTime: Date
) => {
  return {
    ...column,

    cell: (info: CellContext<any, any>) => {
      const raw = info.getValue<string | null | undefined>();
      const formattedBlockTime = formatBlockTimeDisplay(
        info.row.original,
        raw,
        currentAirportTime
      );
      return <span>{formattedBlockTime}</span>;
    },

    filterFn: (row: Row<any>, columnId: string, filterValue: any) => {
      const raw = row.getValue<string | null | undefined>(columnId);
      const formattedBlockTime = formatBlockTimeDisplay(
        row.original,
        raw,
        currentAirportTime
      );

      const searchString = String(filterValue ?? "")
        .toLowerCase()
        .trim();
      if (!searchString) {
        return true;
      }
      return formattedBlockTime.toLowerCase().includes(searchString);
    },
  };
};

/**
 * Updates the configuration of a flight number column by injecting custom cell rendering logic.
 *
 * @returns A new column configuration object with updated cell rendering logic.
 *
 * The custom cell rendering logic generates a `FlightNumberCellLink` component
 * that includes the flight number, airport code, and document ID.
 */
export const updateFlightNumberColumnConfig = (
  column: any,
  airport: string,
  flightType?: "arrival" | "departure"
) => {
  return {
    ...column,
    cell: ({ row, getValue }: { row: Row<any>; getValue: () => any }) => {
      const flightNumber = getValue() as string;
      const docId =
        flightType === "arrival"
          ? row.original.arrival?.docId ?? ""
          : flightType === "departure"
          ? row.original.departure?.docId ?? ""
          : row.original.docId ?? "";
      return FlightNumberCellLink({ airport, flightNumber, docId });
    },
  };
};
